/**
 * @module engine/shapes/ports
 * @summary Per-shape connection-point overrides ("port anchors").
 * @description
 *   Most symbols connect fine at the midpoints of their bounding box, which is
 *   what `getPorts` gives by default. A few — elbows above all — have pipe
 *   mouths that sit nowhere near a bbox midpoint, so a connector docked to the
 *   default port stops in empty canvas. This module lets a shape type declare
 *   where its real faces are, and which way pipework has to leave them.
 *
 *   Anchors are declared in WORLD coords (same convention as the draw fns) and
 *   are un-rotated; `geometry.getPortAnchors` applies `s.rotation`.
 *   // INVARIANT: pure — depends only on the shape passed in.
 */
import type { Shape, PortKey, Point } from '../../types';

/** One connection face: where it is, and the direction pipework leaves it. */
export interface PortAnchor {
  /** Connection point (world coords). */
  p: Point;
  /** Outward unit normal — the direction a connector must run out of the port. */
  n: Point;
}

export type AnchorFn = (s: Shape) => Partial<Record<PortKey, PortAnchor>>;

/**
 * Straight-face ("tangent stub") length of an elbow, as drawn by `D.elbow90`.
 * Shared with the draw fn so the art and the ports can never drift apart.
 */
export function elbowStub(s: Shape): number {
  return Math.min(s.w, s.h) * 0.18;
}

/**
 * A 90° elbow has exactly TWO faces, not four. Un-rotated it turns from an
 * up-facing mouth on the top edge to a right-facing mouth on the right edge, so
 * N/W both resolve to the up mouth and E/S both to the right mouth. Aliasing
 * the two unused cardinals onto the nearest real face means a connector can
 * never end up docked to a point the symbol doesn't actually have.
 */
const elbow90: AnchorFn = (s) => {
  const t = elbowStub(s);
  const up: PortAnchor = { p: { x: s.x + t, y: s.y }, n: { x: 0, y: -1 } };
  const right: PortAnchor = { p: { x: s.x + s.w, y: s.y + s.h - t }, n: { x: 1, y: 0 } };
  return { N: up, W: up, E: right, S: right };
};

/** Shape types with non-default connection faces. */
export const PORT_ANCHORS: Record<string, AnchorFn> = {
  elbow90,
};

/** True if `type` declares its own connection faces (drives stub routing). */
export function hasPortAnchors(type: string): boolean {
  return Object.prototype.hasOwnProperty.call(PORT_ANCHORS, type);
}
